import React from 'react';
import { Link } from 'react-router-dom';
import CopyPrompt from '../Common/CopyPrompt';

const RecruitingWorkforcePage = () => {
  const sampleQueries = [
    "What is the admission status of Jazil Kalim?",
    "What are the required documents for transfer students?",
    "Can you set up a reminder email for missing documents of Jazil?"
  ];

  const sampleEmails = [
    "I recently upgraded my iPhone and need to set up Duo again. Do I have to remove my old phone first, or can I just reactivate Duo on the new device?",
    "When I log in to Okta and Duo on my laptop, I sometimes forget to log out. Is closing the browser tab enough to end my session, or do I need to quit the browser completely to be signed out?",
    "Could you please confirm whether the admissions decisions are released all at once or on a rolling basis within the round?",
    "If I am placed on the waitlist, would there be an opportunity to submit additional materials, such as an updated resume or recommendation letter?",
    "How long does the \"Remember this device for 30 days\" option actually last, and are there any browsers that don't support it?"
  ];

  return (
    <div className="min-h-screen bg-purple-50 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Admissions Status & Email Automation Lab
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
            In this lab, you'll explore how AI agents can assist university admissions teams by automatically drafting personalized email responses, 
            checking real-time admission statuses from portals like <strong>Slate</strong>, and sending reminder emails for missing application materials.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2 mt-4">
            You'll experience how AI can handle email triage and communication automation — the kind of task that would normally take hours of manual staff effort.
          </p>

          <div className="mt-6 mb-4">
            <a 
              href="https://app.campusmind.ai/agents-market" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-base sm:text-lg"
            >
              Try Recruiting Workforce →
            </a>
          </div>
        </div>

        {/* Problem 1: Automated Email Response */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-blue-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">1</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Problem 1: Automated Email Response</h2>
              <p className="text-gray-700 mb-4">Imagine you're a student reaching out to the admissions team with common questions. The university has connected an AI-powered email responder (linked to <strong>classbuddydemo@gmail.com</strong>) that monitors incoming messages, drafts responses, and assists the admissions office.</p>
              
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-blue-900 mb-2">Step 1: Send an Email</h3>
                <p className="text-blue-800 mb-2">Open the email app on your phone (<strong>Gmail</strong>, <strong>Outlook</strong>, or any client) and send an email to:</p>
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <p className="text-lg font-mono text-blue-900">📧 classbuddydemo@gmail.com</p>
                </div>
                <p className="text-blue-800 mb-2">Use any of the following sample questions as your email message:</p>
                <ul className="text-blue-800 space-y-1">
                  {sampleEmails.map((email, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span className="text-sm">{email}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Problem 2: Chat with the Admissions Status Agent */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-green-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">2</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Problem 2: Chat with the Admissions Status Agent</h2>
              <p className="text-gray-700 mb-4">Now let's switch from email to live chat.</p>
              
              {/* Step 1: Find the Agent */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">Step 1: Find the Agent</h3>
                <p className="text-green-800 mb-2">Steps:</p>
                <ul className="text-green-800 space-y-1">
                  <li>• Navigate to the <strong>Marketplace</strong> from the left navigation pane.</li>
                  <li>• Under the <strong>Career Services</strong> category, locate and select <strong>"Admissions Status Agent."</strong></li>
                  <li>• Click <strong>Chat</strong> to begin.</li>
                </ul>
                
                {/* Screenshots */}
                <div className="space-y-4 mt-4">
                  <div className="flex justify-center">
                    <img
                      src="/step1-find-admissions-agent.png"
                      alt="Finding Admissions Agent - Step 1"
                      className="w-full max-w-2xl rounded-lg shadow-md"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/step1b-find-admissions-agent.png"
                      alt="Finding Admissions Agent - Step 1b"
                      className="w-full max-w-2xl rounded-lg shadow-md"
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Ask About an Application */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">Step 2: Ask About an Application</h3>
                <p className="text-green-800 mb-2">Ask the following question:</p>
                <div className="bg-white border border-green-200 rounded p-3 mb-3">
                  <p className="text-lg font-mono text-green-900">"What is the admission status of Jazil Kalim?"</p>
                </div>
                <p className="text-green-800 mb-2">✅ The agent will:</p>
                <ul className="text-green-800 space-y-1">
                  <li>• Search the university's admissions portal (e.g., <strong>Slate</strong>).</li>
                  <li>• Retrieve Jazil's current application status.</li>
                  <li>• Inform you that Jazil is missing a letter of recommendation from the application file.</li>
                </ul>
              </div>

              {/* Step 3: Check Admission Requirements */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">Step 3: Check Admission Requirements</h3>
                <p className="text-green-800 mb-2">Since Jazil is a transfer student, verify whether the letter of recommendation is required.</p>
                <p className="text-green-800 mb-2">Ask:</p>
                <div className="bg-white border border-green-200 rounded p-3 mb-3">
                  <p className="text-lg font-mono text-green-900">"What are the required documents for transfer students?"</p>
                </div>
                <p className="text-green-800 mb-2">✅ The agent will:</p>
                <ul className="text-green-800 space-y-1">
                  <li>• Look up the admissions policy knowledge base.</li>
                  <li>• Return a detailed list of required documents for transfer applicants.</li>
                  <li>• Provide a direct link to the policy document for transparency.</li>
                </ul>
                <p className="text-green-800 mt-2">📘 Based on the result, you'll find that a <strong>letter of recommendation is required</strong> for transfer students.</p>
              </div>

              {/* Step 4: Send a Reminder */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">Step 4: Send a Reminder</h3>
                <p className="text-green-800 mb-2">Now, automate the reminder process.</p>
                <p className="text-green-800 mb-2">Ask:</p>
                <div className="bg-white border border-green-200 rounded p-3 mb-3">
                  <p className="text-lg font-mono text-green-900">"Can you set up a reminder email for missing documents of Jazil?"</p>
                </div>
                <p className="text-green-800 mb-2">✅ The agent will:</p>
                <ul className="text-green-800 space-y-1">
                  <li>• Generate a personalized <strong>draft email</strong> addressed to Jazil.</li>
                  <li>• Include clear instructions and a polite reminder to submit the missing document.</li>
                  <li>• Save this as a <strong>draft email</strong> for review before sending.</li>
                </ul>
              </div>

              {/* Step 5: Review the Outputs */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">Step 5: Review the Outputs</h3>
                <p className="text-green-800 mb-2">Now that the agent has handled both email responses and admission checks, verify its work.</p>
                <p className="text-green-800 mb-2">Steps:</p>
                <ol className="text-green-800 space-y-1 mb-3">
                  <li>1. Open <strong>Gmail</strong> in your browser.</li>
                  <li>2. Go to your <strong>Inbox</strong>:</li>
                  <li className="ml-4">• Locate the email you sent earlier in Problem 1.</li>
                  <li className="ml-4">• Open it and review the AI-generated draft reply to see how it addresses your question.</li>
                  <li>3. Go to your <strong>Drafts</strong> folder:</li>
                  <li className="ml-4">• Find the draft reminder email created for Jazil regarding the missing letter of recommendation.</li>
                  <li className="ml-4">• Review the subject line, tone, and completeness of the message.</li>
                </ol>
                <p className="text-green-800 mt-2">💡 This demonstrates how AI combines <strong>conversation data</strong> and <strong>backend records</strong> to generate personalized communications automatically.</p>
                
                {/* Screenshot */}
                <div className="flex justify-center mt-4">
                  <img
                    src="/step5-review-outputs.png"
                    alt="Review Outputs - Step 5"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wrap-Up */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Wrap-Up
          </h2>
          <div className="space-y-4">
            <p className="text-lg text-gray-700">🎉 Great job! You've just seen how AI agents can:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span>Handle real email queries automatically.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span>Retrieve real-time information from portals like <strong>Slate</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span>Create personalized follow-up messages without manual effort.</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 mt-4">
              Imagine your admissions inbox flooded with thousands of emails from applicants — this AI agent can respond to all of them intelligently and instantly, 
              while also sending customized reminders to students based on their individual profiles.
            </p>
            <p className="text-lg font-bold text-green-700 mt-4">
              🚀 That's the power of <strong>AI-driven admissions automation</strong>!
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/it-helpdesk-agent"
            className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200"
          >
            Back: IT Workforce
          </Link>
          <Link
            to="/explore-marketplace"
            className="bg-purple-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors duration-200"
          >
            Next: Explore Marketplace
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RecruitingWorkforcePage;
